import * as React from "react";
import { CardContent } from "../../../components/antd/card/CardContent";
import { Button, Space, Tooltip } from "antd";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { formSet, formSetValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { store } from "../../../store";
import { CpLabelTooltip } from "../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../gvc/testNewForm/CpTooltip";
import { ImportButtonRaw, DropZone } from "../../../components/import";
import { SecretCodeEditorObfuscated } from "./SecretCodeEditorObfuscated";

interface Props {}
export const SecretDataKeypair: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const secretKey = useCpFormValue(`keypair.secretKey.value`);
  const publicKey = useCpFormValue(`keypair.publicKey`);
  const isPassphraseVisible = useCpFormValue(`keypair.passphrase.isVisible`);

  return (
    <>
      <CardContent>
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-2">
            <div>Secret Key</div>
            <ImportButtonRaw
              formId={id}
              fieldName="keypair.secretKey.value"
              actionLabel="Import secret key"
              accept=".pem,.key,.txt"
              existingHasContent={!!secretKey?.trim()}
              explanation={
                <div className="text-xs">
                  <div className="font-semibold mb-1">Accepted formats:</div>
                  <div><strong>.pem, .key</strong> — Private key file</div>
                  <div className="mt-1 opacity-75">PEM-encoded RSA/EC private key</div>
                </div>
              }
            />
          </div>
          <SecretCodeEditorObfuscated visibilityName="meta.codeEditorVisibility.keypair.secretKey" contentName="keypair.secretKey.value">
            <DropZone
              formId={id}
              fieldName="keypair.secretKey.value"
              actionLabel="Import secret key"
              accept={[".pem", ".key", ".txt"]}
            >
              <CodeEditor
                value={secretKey}
                setValue={(value) => {
                  formSetValue(id, `keypair.secretKey.value`, value);
                }}
                onChange={() => {
                  const name = "keypair.secretKey.value";
                  formSet(id, `fieldStates.${name}.modified`, true);

                  const form = store.getState().form.instances[id];
                  const onChangeCounter = form.meta.onChangeCounter;
                  formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
                }}
                onFocus={() => {
                  const name = "keypair.secretKey.value";
                  formSet(id, `fieldStates.${name}.focused`, true);
                  formSet(id, `fieldStates.${name}.visited`, true);
                }}
                onBlur={() => {
                  const name = "keypair.secretKey.value";
                  formSet(id, `fieldStates.${name}.focused`, false);
                  formSet(id, `fieldStates.${name}.touched`, true);

                  const form = store.getState().form.instances[id];
                  const onBlurCounter = form.meta.onBlurCounter;
                  formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
                }}
                language={"javascript"}
                options={{
                  basicSetup: {
                    lineNumbers: true,
                  },
                  style: {
                    maxHeight: 500,
                    overflow: "auto",
                    fontSize: 14,
                  },
                }}
              />
            </DropZone>
          </SecretCodeEditorObfuscated>
          <CpErrorWarning name={`keypair.secretKey.value`} />
        </div>
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-2">
            <div>Public Key</div>
            <ImportButtonRaw
              formId={id}
              fieldName="keypair.publicKey"
              actionLabel="Import public key"
              accept=".pem,.pub,.key,.txt"
              existingHasContent={!!publicKey?.trim()}
              explanation={
                <div className="text-xs">
                  <div className="font-semibold mb-1">Accepted formats:</div>
                  <div><strong>.pem, .pub, .key</strong> — Public key file</div>
                  <div className="mt-1 opacity-75">PEM-encoded or OpenSSH public key</div>
                </div>
              }
            />
          </div>
          <SecretCodeEditorObfuscated visibilityName="meta.codeEditorVisibility.keypair.publicKey" contentName="keypair.publicKey">
            <DropZone
              formId={id}
              fieldName="keypair.publicKey"
              actionLabel="Import public key"
              accept={[".pem", ".pub", ".key", ".txt"]}
            >
              <CodeEditor
                value={publicKey}
                setValue={(value) => formSetValue(id, `keypair.publicKey`, value)}
                onChange={() => {
                  const name = "keypair.publicKey";
                  formSet(id, `fieldStates.${name}.modified`, true);

                  const form = store.getState().form.instances[id];
                  const onChangeCounter = form.meta.onChangeCounter;
                  formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
                }}
                onFocus={() => {
                  const name = "keypair.publicKey";
                  formSet(id, `fieldStates.${name}.focused`, true);
                  formSet(id, `fieldStates.${name}.visited`, true);
                }}
                onBlur={() => {
                  const name = "keypair.publicKey";
                  formSet(id, `fieldStates.${name}.focused`, false);
                  formSet(id, `fieldStates.${name}.touched`, true);

                  const form = store.getState().form.instances[id];
                  const onBlurCounter = form.meta.onBlurCounter;
                  formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
                }}
                language={"javascript"}
                options={{
                  basicSetup: {
                    lineNumbers: true,
                  },
                  style: {
                    maxHeight: 500,
                    overflow: "auto",
                    fontSize: 14,
                  },
                }}
              />
            </DropZone>
          </SecretCodeEditorObfuscated>
        </div>
        <CpFormItem>
          <CpLabelTooltip>
            <CpLabel name={`keypair.passphrase.value`}>Passphrase</CpLabel>
            <CpTooltip tooltipProps={{ title: `Passphrase is required if provided secret key is encrypted.` }} />
          </CpLabelTooltip>
          <Space.Compact>
            <CpInput
              name={`keypair.passphrase.value`}
              type={isPassphraseVisible ? "text" : `password`}
              style={{ width: 600 }}
            />
            <Tooltip title={isPassphraseVisible ? "Hide" : "Show"}>
              <Button
                onClick={() => formSetValue(id, `keypair.passphrase.isVisible`, !isPassphraseVisible)}
                icon={isPassphraseVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              />
            </Tooltip>
          </Space.Compact>
          <CpErrorWarning name={`keypair.passphrase.value`} />
        </CpFormItem>
      </CardContent>
    </>
  );
};
